import { useEffect, useState } from "react";

import { AnnotationSource } from "@perceptron/core/types";
import { Icon } from "@perceptron/ui/components/icon";
import { cn } from "@perceptron/utils/cn";

import { useAnnotationInteractions } from "@demo/hooks/annotations/useAnnotationInteractions";
import { zoomAnnotationId } from "@demo/libs/tool-calls/zoom";
import { useToolCallStore, type ToolCallEntry } from "@demo/store/tool-calls";
import { strings } from "@demo/utils/strings";

export interface ZoomRegionTextProps {
  /** A `zoom` family call; its `input.image` carries the region and the model's label for it. */
  call: ToolCallEntry;
}

/** The selection's zoom takes 50ms to start and 500ms to run; the undo shows once it has settled. */
const ZOOM_ANIMATION_MS = 550;

/**
 * The chip for a region the model zoomed into: the Zoom pill, the model's label for the region, and a
 * play button that selects the region on the image (`zoomAnnotations` in `store/tool-calls.ts`), where it
 * is drawn with corner brackets as the view zooms to it; once there, the button undoes the selection.
 * A whole-image zoom has no region to select and shows the chip alone.
 */
function ZoomRegionText({ call }: ZoomRegionTextProps) {
  const { handleClick, clickedAnnotationId, setClickedAnnotationId, setHighlightedAnnotationIds } =
    useAnnotationInteractions();
  const copy = strings.toolCall.zoom;
  const image = call.input?.kind === "image" ? call.input.image : undefined;
  const hasRegion = !!image?.region;
  const displayLabel = image?.label?.trim() || copy.label;
  const zoomId = zoomAnnotationId(call.id);
  const isClicked = clickedAnnotationId === zoomId;

  // Slot contents remount as the reasoning streams: a chip that mounts already selected is past its zoom,
  // and one that has entered this run keeps still rather than fading in again.
  const [animationCompleted, setAnimationCompleted] = useState(isClicked);
  useEffect(() => {
    if (!isClicked) {
      setAnimationCompleted(false);
      return;
    }
    const timer = setTimeout(() => setAnimationCompleted(true), ZOOM_ANIMATION_MS);
    return () => clearTimeout(timer);
  }, [isClicked]);
  const [fadeIn] = useState(() => !useToolCallStore.getState().entered.has(call.id));
  useEffect(() => {
    useToolCallStore.getState().entered.add(call.id);
  }, [call.id]);

  const isZooming = isClicked && !animationCompleted;
  const select = () => handleClick(zoomId, AnnotationSource.CONTENT);
  const handlePlayClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    select();
  };
  const handleUndoClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setClickedAnnotationId(undefined, undefined);
    setHighlightedAnnotationIds(undefined, undefined);
  };

  const isActive = isClicked && animationCompleted;
  const tint = isActive ? "text-foreground-primary" : "text-foreground-secondary group-hover:text-foreground-primary";

  return (
    <div
      className={cn(
        "group my-2 flex w-full items-center gap-3 rounded-[40px] border p-1 transition-all duration-200",
        fadeIn && "animate-fade-in",
        hasRegion && "cursor-pointer",
        isActive
          ? "bg-foreground-primary/30 border-foreground-primary shadow-[0_0_8px_rgba(255,255,255,0.4)]"
          : "bg-foreground-secondary/10 hover:bg-foreground-primary/30 border-foreground-secondary hover:border-foreground-primary hover:shadow-[0_0_8px_rgba(255,255,255,0.4)]",
      )}
      data-testid="zoom-region"
      data-tool-call-id={call.id}
      // While the view is still zooming, the chip waits like its button does.
      onClick={hasRegion && !isZooming ? select : undefined}
    >
      <div className="flex items-center gap-2 rounded-3xl bg-white/15 px-2 py-1">
        <Icon className={cn("h-4 w-4 transition-colors", tint)} name="scan-eye" />
        <span className={cn("transition-colors label-2", tint)}>{copy.label}</span>
      </div>
      <span className={cn("ml-auto transition-colors label-2", tint)} data-testid="zoom-region-label">
        {displayLabel}
      </span>
      {hasRegion && (
        <button
          aria-label={isActive ? copy.undo : copy.play}
          className={cn(
            "ml-2 mr-1 flex items-center justify-center rounded-full p-1.5 transition-colors",
            isZooming ? "opacity-50" : "hover:bg-white/10",
          )}
          data-testid="zoom-region-button"
          disabled={isZooming}
          type="button"
          onClick={isActive ? handleUndoClick : handlePlayClick}
        >
          <Icon className={cn("h-4 w-4 transition-colors", tint)} name={isActive ? "undo-2" : "play"} />
        </button>
      )}
    </div>
  );
}

export default ZoomRegionText;
